import { AgentPublicConfigSchema, type AgentPublicConfig } from '@cloneai/shared-types';
import { logger, metrics } from '@cloneai/observability';
import * as Sentry from '@sentry/react';
import { type JSX, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { AgentStage } from './components/AgentStage.js';
import { fetchAgentConfigResponse } from './agentConfigFetch.js';
import { env } from './env.js';
import { useQueuedAnimation } from './hooks/useQueuedAnimation.js';
import { useTextChat } from './hooks/useTextChat.js';
import { useVoiceSession } from './hooks/useVoiceSession.js';
import {
  getRuntimeLabels,
  languageStorageKey,
  resolveInitialLanguage,
  selectLocalizedConfig,
} from './language.js';
import { emit } from './postMessageProtocol.js';
import { agentReducer, initialState, type AgentState } from './state.js';
import { SessionRecorder } from './voice/sessionRecorder.js';

interface Slugs {
  tenantSlug: string | null;
  agentSlug: string | null;
}

/**
 * Parse `{tenantSlug, agentSlug}` from the current URL.
 *
 * Two URL schemes supported in that order of precedence:
 *
 * 1. Dev/local override — `VITE_DEV_TENANT_SLUG` + `VITE_DEV_AGENT_SLUG`
 *    env vars. Lets you hit `http://localhost:5175/` directly without any
 *    routing magic.
 *
 * 2. Production — custom-domain shape `<tenantSlug>.agent.cloneai.ca/<agentSlug>`.
 *    `tenantSlug` is the first label of the hostname; `agentSlug` is the
 *    first path segment. Requires the `*.agent.cloneai.ca` wildcard + ACM
 *    cert to be in place (Task 43 cutover).
 *
 * 3. Placeholder mode — bare CloudFront URL like
 *    `https://<distId>.cloudfront.net/<tenantSlug>/<agentSlug>`. The custom
 *    domain isn't wired up yet, so we fall back to taking BOTH slugs from
 *    the path. Detected by the hostname failing to match the `*.agent.*`
 *    shape. Removed automatically after Task 43 cutover — production URLs
 *    never hit this branch.
 */
function parseSlugs(): Slugs {
  const devTenant = import.meta.env.VITE_DEV_TENANT_SLUG;
  const devAgent = import.meta.env.VITE_DEV_AGENT_SLUG;
  if (devTenant) {
    return {
      tenantSlug: devTenant,
      agentSlug: devAgent ?? null,
    };
  }

  const host = window.location.host; // e.g. "acme.agent.cloneai.ca" or "d1abc.cloudfront.net"
  const hostname = host.split(':')[0] ?? '';
  const parts = hostname.split('.');
  const pathSegments = window.location.pathname.split('/').filter(Boolean);

  // Custom-domain shape: at least 4 labels AND the second label is `agent`.
  // e.g. `acme.agent.cloneai.ca` → tenant `acme`, agent from path[0].
  if (parts.length >= 4 && parts[1] === 'agent') {
    return {
      tenantSlug: parts[0] ?? null,
      agentSlug: pathSegments[0] ?? null,
    };
  }

  // Placeholder mode — bare CloudFront / execute-api / localhost host without
  // the wildcard subdomain. Both slugs come from the path:
  //   https://<distId>.cloudfront.net/<tenantSlug>/<agentSlug>
  return {
    tenantSlug: pathSegments[0] ?? null,
    agentSlug: pathSegments[1] ?? null,
  };
}

/**
 * Detect embed mode from the current URL (`?embed=1`). Read once at mount;
 * we never react to history changes because the agent runtime does no
 * client-side routing.
 */
function detectEmbed(): boolean {
  return new URLSearchParams(window.location.search).get('embed') === '1';
}

export function App(): JSX.Element {
  const [state, dispatch] = useReducer(agentReducer, initialState);
  const [slugs] = useState<Slugs>(parseSlugs);
  const [isEmbed] = useState<boolean>(detectEmbed);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [configLoadAttempt, setConfigLoadAttempt] = useState<number>(0);
  const [selectedLang, setSelectedLang] = useState<string | null>(null);
  const sessionStartRef = useRef<number | null>(null);
  const textChatStartedRef = useRef<boolean>(false);
  const pageLoadedAtRef = useRef<string>(new Date().toISOString());
  // Page-lifecycle session recorder. Constructed once we have valid slugs,
  // but it does not create the public session until the user starts playback.
  // Events before play (page load, language selector changes) are buffered
  // and flushed onto the same timeline once the play gesture starts a session.
  // The voice-side OpenAIRealtimeSession reuses this same instance via
  // useVoiceSession (window.__cloneai_recorder).
  const recorderRef = useRef<SessionRecorder | null>(null);

  // Embed-mode body class hook. Styles in index.css key off `body.embed`.
  useEffect(() => {
    if (isEmbed) {
      document.body.classList.add('embed');
      return () => {
        document.body.classList.remove('embed');
      };
    }
    document.body.classList.remove('embed');
    return undefined;
  }, [isEmbed]);

  // Inbound postMessages are not handled in M1.
  // Extension point: add a 'message' event listener here in M2+ for host-controlled actions.

  useEffect(() => {
    Sentry.setTags({
      tenantSlug: slugs.tenantSlug ?? 'none',
      agentSlug: slugs.agentSlug ?? 'none',
      mode: isEmbed ? 'embed' : 'standalone',
    });
  }, [isEmbed, slugs.agentSlug, slugs.tenantSlug]);

  // Forward CTA clicks (dispatched as a window CustomEvent by AgentStage) to the host.
  useEffect(() => {
    function onCtaClick(e: Event): void {
      const ce = e as CustomEvent<{ ctaId: string; url: string }>;
      if (!ce.detail) return;
      emit({ type: 'cloneai:cta-click', ctaId: ce.detail.ctaId, url: ce.detail.url });
      recorderRef.current?.recordImmediateEvent({
        type: 'cta-click',
        timestamp: new Date().toISOString(),
        payload: { ctaId: ce.detail.ctaId, url: ce.detail.url },
      });
    }
    window.addEventListener('cta-click', onCtaClick);
    return () => {
      window.removeEventListener('cta-click', onCtaClick);
    };
  }, []);

  // Session-start / session-end lifecycle bridge.
  useEffect(() => {
    if (
      (state.status === 'voice-active' || state.status === 'text-active') &&
      sessionStartRef.current === null
    ) {
      sessionStartRef.current = Date.now();
      emit({ type: 'cloneai:session-start' });
    } else if (state.status === 'ended' && sessionStartRef.current !== null) {
      const durationSec = Math.round((Date.now() - sessionStartRef.current) / 1000);
      sessionStartRef.current = null;
      emit({ type: 'cloneai:session-end', durationSec });
    }
  }, [state.status]);

  const handleRuntimeStart = useCallback((details: { hasVideo: boolean }): void => {
    const r = recorderRef.current;
    if (!r) return;
    r.recordEvent({
      type: 'video-start',
      timestamp: new Date().toISOString(),
      payload: { source: 'play-button', hasVideo: details.hasVideo },
    });
    void r.start();
  }, []);

  // Page-lifecycle events flow into the recorder: video-complete when the
  // intro ends. The play gesture itself is recorded synchronously from the
  // Start button handler so no-video agents and React batched updates still
  // start the public session at the click.
  useEffect(() => {
    const r = recorderRef.current;
    if (!r) return;
    if (state.status === 'minting-session') {
      // Video finished, voice session about to mint.
      r.recordEvent({ type: 'video-complete', timestamp: new Date().toISOString() });
    } else if (state.status === 'text-active') {
      if (!textChatStartedRef.current) {
        textChatStartedRef.current = true;
        if (state.config.interactionMode === 'text') {
          r.recordEvent({ type: 'video-complete', timestamp: new Date().toISOString() });
        }
        r.recordEvent({ type: 'text-chat-start', timestamp: new Date().toISOString() });
      }
    } else if (state.status === 'ended') {
      if (textChatStartedRef.current) {
        textChatStartedRef.current = false;
        r.recordEvent({
          type: 'text-chat-end',
          timestamp: new Date().toISOString(),
          payload: { reason: 'completed' },
        });
      }
      // Best-effort end on terminal state. Idempotent server-side.
      void r.close({ kind: 'completed' });
    }
  }, [state.status]);

  useEffect(() => {
    const { tenantSlug, agentSlug } = slugs;
    if (!tenantSlug || !agentSlug) {
      dispatch({ type: 'NOT_FOUND', reason: 'not-found' });
      return;
    }

    const controller = new AbortController();
    const tenant: string = tenantSlug;
    const agent: string = agentSlug;

    async function load(): Promise<void> {
      const startMs = performance.now();
      try {
        const url = `${env.VITE_API_BASE_URL.replace(/\/$/, '')}/v1/public/agents/${encodeURIComponent(
          tenant,
        )}/${encodeURIComponent(agent)}`;
        const res = await fetchAgentConfigResponse(url, { signal: controller.signal });

        if (res.status === 404) {
          dispatch({ type: 'NOT_FOUND', reason: 'not-found' });
          return;
        }
        if (res.status === 410) {
          dispatch({ type: 'NOT_FOUND', reason: 'archived' });
          return;
        }
        if (res.status === 503) {
          // Backend may return `{ name: '...' }` in the paused payload. Fall
          // back to a neutral label if the body doesn't include one.
          let agentName = 'This agent';
          try {
            const body: unknown = await res.json();
            if (
              body !== null &&
              typeof body === 'object' &&
              'name' in body &&
              typeof (body as { name: unknown }).name === 'string'
            ) {
              agentName = (body as { name: string }).name;
            }
          } catch {
            /* ignore — fall back to default */
          }
          dispatch({ type: 'PAUSED', agentName });
          return;
        }

        if (!res.ok) {
          throw new Error(`Unexpected status ${res.status} from public agent config`);
        }

        const json: unknown = await res.json();
        const parsed = AgentPublicConfigSchema.safeParse(json);
        if (!parsed.success) {
          throw new Error(`Malformed AgentPublicConfig: ${parsed.error.message}`);
        }
        logger.info('agent public config loaded', {
          tenantSlug: tenant,
          agentSlug: agent,
          agentId: parsed.data.agentId,
          mode: isEmbed ? 'embed' : 'standalone',
        });
        metrics.timing('agent.config.load.duration', performance.now() - startMs, {
          tags: { tenantSlug: tenant, agentSlug: agent, status: 'ok' },
        });
        const storageKey = languageStorageKey(tenant, agent);
        let storedLang: string | null = null;
        try {
          storedLang = window.localStorage.getItem(storageKey);
        } catch {
          storedLang = null;
        }
        const initialLang = resolveInitialLanguage(parsed.data, {
          queryLang: new URLSearchParams(window.location.search).get('lang'),
          storedLang,
          browserLanguages: window.navigator.languages,
        });
        setSelectedLang(initialLang);
        dispatch({ type: 'CONFIG_LOADED', config: parsed.data });
        emit({ type: 'cloneai:ready', agentId: parsed.data.agentId });

        // Construct the page-lifecycle recorder. It buffers `view` now, but
        // does not POST /sessions until the user hits play. `useVoiceSession`
        // reuses this instance so token usage + transcript turns flow through
        // the same buffer once the playback gesture starts the session.
        if (!recorderRef.current) {
          const recorder = new SessionRecorder({
            apiBaseUrl: env.VITE_API_BASE_URL,
            tenantSlug: tenant,
            productSlug: agent,
            productType: 'agent',
            origin: window.location.origin,
          });
          recorderRef.current = recorder;
          // Expose for the voice session hook — keeps the surface narrow
          // (no prop-drilling through AgentStage).
          (window as unknown as { __cloneai_recorder?: SessionRecorder }).__cloneai_recorder =
            recorder;
          recorder.recordEvent({
            type: 'view',
            timestamp: pageLoadedAtRef.current,
            payload: { source: 'page-load', lang: initialLang },
          });
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        metrics.increment('agent.config.load_failed', 1, {
          tags: { tenantSlug: tenant, agentSlug: agent },
        });
        metrics.timing('agent.config.load.duration', performance.now() - startMs, {
          tags: { tenantSlug: tenant, agentSlug: agent, status: 'error' },
        });
        Sentry.captureException(err);
        const message = err instanceof Error ? err.message : 'Unknown error';
        emit({ type: 'cloneai:error', code: 'config-load-failed', message });
        setFatalError(getRuntimeLabels(window.navigator.language).configLoadFailed);
      }
    }

    void load();
    return () => controller.abort();
  }, [configLoadAttempt, isEmbed, slugs]);

  // Prepare the voice transport during a user-started intro. Microphone
  // capture and the opening greeting remain gated on the end of the video.
  const isActive = state.status === 'minting-session' || state.status === 'voice-active';
  const isTextActive = state.status === 'text-active';
  const rawConfig =
    state.status === 'ready' ||
    state.status === 'playing-video' ||
    state.status === 'minting-session' ||
    state.status === 'voice-active' ||
    state.status === 'text-active' ||
    state.status === 'ended'
      ? state.config
      : null;
  const localizedConfig = useMemo(
    () => (rawConfig ? selectLocalizedConfig(rawConfig, selectedLang) : null),
    [rawConfig, selectedLang],
  );
  const labels = getRuntimeLabels(selectedLang ?? window.navigator.language);

  const handleLanguageChange = useCallback(
    (nextLang: string): void => {
      if (!rawConfig || !rawConfig.languages.includes(nextLang)) return;
      const fromLang = selectedLang ?? rawConfig.defaultLang;
      if (nextLang === fromLang) return;

      setSelectedLang(nextLang);
      if (slugs.tenantSlug && slugs.agentSlug) {
        try {
          window.localStorage.setItem(
            languageStorageKey(slugs.tenantSlug, slugs.agentSlug),
            nextLang,
          );
        } catch {
          /* ignore storage failures */
        }
      }
      recorderRef.current?.recordEvent({
        type: 'language-change',
        timestamp: new Date().toISOString(),
        payload: { fromLang, toLang: nextLang, source: 'selector' },
      });
      Sentry.addBreadcrumb({
        category: 'agent.language',
        message: 'language changed',
        level: 'info',
        data: { fromLang, toLang: nextLang, source: 'selector' },
      });
      metrics.increment('agent.language.changed', 1, {
        tags: { fromLang, toLang: nextLang, source: 'selector' },
      });
    },
    [rawConfig, selectedLang, slugs.agentSlug, slugs.tenantSlug],
  );

  // Side-effect: once config loads, update <title>, <meta description>, and favicon.
  useEffect(() => {
    if (localizedConfig) {
      applyDocumentMetadata(localizedConfig);
    }
  }, [localizedConfig]);

  const voiceSession = useVoiceSession(
    localizedConfig,
    slugs.tenantSlug,
    slugs.agentSlug,
    selectedLang,
    isActive,
    dispatch,
    state.status === 'playing-video' &&
      localizedConfig?.interactionMode === 'voice' &&
      localizedConfig.voiceProvider === 'openai-realtime',
  );

  const queuedAnimation = useQueuedAnimation(
    isTextActive,
    recorderRef,
    localizedConfig
      ? `${localizedConfig.agentId}:${selectedLang ?? localizedConfig.defaultLang}`
      : null,
  );

  const textChat = useTextChat(
    localizedConfig,
    slugs.tenantSlug,
    slugs.agentSlug,
    selectedLang,
    isTextActive,
    recorderRef,
    {
      canSubmit: !queuedAnimation.isBusy,
      onTurnStarted: queuedAnimation.discardReplay,
      onAnimationQueued: queuedAnimation.start,
    },
  );

  const handleSwitchToText = useCallback((): void => {
    if (!rawConfig || !rawConfig.textChatFallbackEnabled) return;
    recorderRef.current?.recordEvent({
      type: 'communication-method-switch',
      timestamp: new Date().toISOString(),
      payload: { from: 'voice', to: 'text', source: 'fallback-button' },
    });
    dispatch({ type: 'SWITCH_TO_TEXT' });
  }, [rawConfig]);

  if (fatalError) {
    return (
      <Message
        title={labels.genericErrorTitle}
        body={fatalError}
        actionLabel={labels.retry}
        onAction={() => {
          setFatalError(null);
          setConfigLoadAttempt((attempt) => attempt + 1);
        }}
      />
    );
  }

  switch (state.status) {
    case 'loading':
      return <LoadingView label={labels.loading} />;
    case 'not-found':
      return (
        <Message
          testId="not-found"
          title={state.reason === 'archived' ? labels.archivedTitle : labels.notFoundTitle}
          body={state.reason === 'archived' ? labels.archivedBody : labels.notFoundBody}
        />
      );
    case 'paused':
      return <Message title={labels.pausedTitle} body={labels.pausedBody(state.agentName)} />;
    case 'ready':
    case 'playing-video':
    case 'minting-session':
    case 'voice-active':
    case 'text-active':
    case 'ended':
      return (
        <AgentStage
          config={localizedConfig ?? state.config}
          state={state}
          dispatch={dispatch}
          voiceSession={voiceSession}
          selectedLang={selectedLang ?? state.config.defaultLang}
          languages={state.config.languages}
          labels={labels}
          textChat={textChat}
          animation={queuedAnimation}
          onLanguageChange={handleLanguageChange}
          onRuntimeStart={handleRuntimeStart}
          onSwitchToText={handleSwitchToText}
        />
      );
  }
}

function LoadingView({ label }: { label: string }): JSX.Element {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#000',
      }}
    >
      <div
        aria-label={label}
        role="status"
        style={{
          width: 48,
          height: 48,
          border: '4px solid rgba(255,255,255,0.2)',
          borderTopColor: '#fff',
          borderRadius: '50%',
          animation: 'cloneai-agent-spin 1s linear infinite',
        }}
      />
    </div>
  );
}

function Message({
  title,
  body,
  testId,
  actionLabel,
  onAction,
}: {
  title: string;
  body: string;
  testId?: string;
  actionLabel?: string;
  onAction?: () => void;
}): JSX.Element {
  return (
    <div
      data-testid={testId}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 24,
        textAlign: 'center',
        background: '#000',
        color: '#fff',
      }}
    >
      <h1 style={{ fontSize: 24, margin: 0 }}>{title}</h1>
      <p style={{ opacity: 0.8, margin: 0, maxWidth: 480 }}>{body}</p>
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          style={{
            marginTop: 8,
            border: 0,
            borderRadius: 999,
            padding: '10px 20px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function applyDocumentMetadata(config: AgentPublicConfig): void {
  document.title = `${config.name} | ${config.brand.name}`;

  const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (description) {
    description.content = `Talk to ${config.name} from ${config.brand.name}.`;
  }

  const iconUrl = config.brand.logoUrl ?? config.posterUrl;
  if (iconUrl) {
    let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!icon) {
      icon = document.createElement('link');
      icon.rel = 'icon';
      document.head.appendChild(icon);
    }
    icon.href = iconUrl;
  }
}

// Re-export so consumers can access the state type if needed.
export type { AgentState };
